import { ResponseStatusEnum } from "../../../helpers/GlobalEnum";
import { alertedProductsServices } from "../../../helpers/services/AlertedProductsServices";
import { nextRowKey } from "../../../shared/lib/rowKey";

const buildSolicitudesQuery = ({
  idEncabezado,
  estadoSolicitud,
  tipoSolicitud,
  page = 1,
  pageSize = 10,
} = {}) => {
  const params = new URLSearchParams();

  if (String(idEncabezado ?? "").trim()) {
    params.set("encabezado_id", String(idEncabezado).trim());
  }

  if (estadoSolicitud?.value != null) {
    params.set("estado_id", String(estadoSolicitud.value));
  }

  if (tipoSolicitud?.value != null) {
    params.set("gestion_id", String(tipoSolicitud.value));
  }

  params.set("page", String(page));
  params.set("size", String(pageSize));

  const queryString = params.toString();
  return queryString ? `?${queryString}` : "";
};

// Fila de /api/alertas/productos/encabezados. El "estado" (workflow) alimenta la
// columna/pill de "Gestión alerta" y controla las acciones (mismo id que usaba
// gestion_alerta). El id_encabezado es la identidad del renglón.
const normalizeSolicitudRow = (row = {}) => ({
  id: row?.id_encabezado ?? null,
  idEncabezado: row?.id_encabezado ?? null,
  tipoGestion: row?.tipo_gestion?.nombre ?? "",
  tipoGestionCodigo: row?.tipo_gestion?.id ?? null,
  gestionAlerta: row?.estado?.nombre ?? "",
  gestionAlertaCodigo: row?.estado?.id ?? null,
  observacion: row?.observacion ?? "",
  activo: row?.activo ?? true,
  usuarioCrea: row?.usario_crea?.correo ?? "",
  fechaSolicitud: row?.fecha_crea ?? "",
});

const normalizeProductoAsociado = (producto = {}) => ({
  ...producto,
  // Key sintético para la tabla; la identidad real sigue en id_orden_detalle.
  rowKey: nextRowKey(),
  id_orden_detalle:
    producto?.id_orden_detalle ??
    producto?.orden_detalle_id ??
    producto?.id_detalle ??
    producto?.id ??
    null,
  // "Nuevo producto" para el flujo INDETERMINADO: nombre + unidad + marca
  // sugeridos. `unidad_sugerida` llega como id (no como objeto); el nombre de la
  // unidad se resuelve en la vista contra el recurso /api/lista/unidades/, por lo
  // que aquí solo se normaliza el id.
  unidadSugeridaId: producto?.unidad_sugerida?.id ?? producto?.unidad_sugerida ?? null,
});

const normalizeDocumento = (doc = {}) => {
  const isPdf = Number(doc?.tipo_archivo) === 2
    || String(doc?.nombre_archivo ?? "").toLowerCase().endsWith(".pdf");
  return {
    id: doc?.id,
    nombre: doc?.nombre_archivo ?? "",
    descripcion: doc?.descripcion ?? "",
    usuarioCreador: doc?.usuario_creador ?? "",
    fechaCreacion: doc?.fecha_creacion ?? "",
    rutaArchivo: doc?.ruta_archivo ?? "",
    esPdf: isPdf,
    puedeVer: isPdf,
    puedeDescargar: true,
  };
};

export const subsanarAlertedProductsSolicitud = async ({
  idEncabezado,
  observacion,
  documento = null,
  productosAsociados = null,
  producto = null,
}) => {
  const formData = new FormData();
  formData.append("id_encabezado", String(idEncabezado));
  formData.append("observacion", observacion);
  if (documento) formData.append("documento", documento);
  if (Array.isArray(productosAsociados)) {
    formData.append("productos_asociados", JSON.stringify(productosAsociados));
  }
  // PRODUCTO INDETERMINADO gestiona un único item: se envía como objeto `producto`.
  if (producto) {
    formData.append("producto", JSON.stringify(producto));
  }

  const response = await alertedProductsServices.subsanarSolicitud(formData);

  if (
    response?.status !== ResponseStatusEnum.OK &&
    response?.status !== ResponseStatusEnum.CREATED
  ) {
    throw response;
  }

  return response?.data ?? {};
};

export const agregarProductoAlertedProductsSolicitud = async ({ idEncabezado, idOrdenDetalle }) => {
  const response = await alertedProductsServices.agregarProductoSolicitud({
    id_encabezado: idEncabezado,
    orden_detalle_id: idOrdenDetalle,
  });

  if (
    response?.status !== ResponseStatusEnum.OK &&
    response?.status !== ResponseStatusEnum.CREATED
  ) {
    throw response;
  }

  return response?.data ?? {};
};

export const eliminarProductoAlertedProductsSolicitud = async ({ idEncabezado, idOrdenDetalle }) => {
  const response = await alertedProductsServices.eliminarProductoSolicitud({
    id_encabezado: idEncabezado,
    orden_detalle_id: idOrdenDetalle,
  });

  if (
    response?.status !== ResponseStatusEnum.OK &&
    response?.status !== ResponseStatusEnum.CREATED &&
    response?.status !== ResponseStatusEnum.NO_CONTENT
  ) {
    throw response;
  }

  return response?.data ?? {};
};

export const gestionarAlertedProductsSolicitud = async ({ idEncabezado, idEstado, observacion, documento = null, producto = null }) => {
  const formData = new FormData();
  formData.append("id_encabezado", String(idEncabezado));
  formData.append("id_estado", String(idEstado));
  formData.append("observacion", observacion);
  if (documento) formData.append("documento_observacion", documento);
  // PRODUCTO INDETERMINADO: al aprobar se envía el producto con los valores recalculados.
  if (producto) formData.append("producto", JSON.stringify(producto));

  const response = await alertedProductsServices.gestionarSolicitud(formData);

  if (
    response?.status !== ResponseStatusEnum.OK &&
    response?.status !== ResponseStatusEnum.CREATED
  ) {
    throw response;
  }

  return response?.data ?? {};
};

export const getAlertedProductsSolicitudDetalle = async (idEncabezado) => {
  const response = await alertedProductsServices.getSolicitudDetalle(idEncabezado);

  if (response?.status !== ResponseStatusEnum.OK) {
    throw response;
  }

  const data = response?.data ?? {};
  return {
    idEncabezado: data?.id_encabezado,
    resumen: data?.resumen ?? "",
    estadoSolicitud: data?.estado_solicitud ?? null,
    // usuario_origen llega como objeto { usuario, correo }; se muestra el usuario
    // y, si no viene, el correo. Compatible con el formato antiguo (string).
    usuarioOrigen:
      typeof data?.usuario_origen === "object" && data?.usuario_origen !== null
        ? data.usuario_origen.usuario || data.usuario_origen.correo || ""
        : data?.usuario_origen ?? "",
    fechaImplementacion: data?.fecha_implementacion ?? "",
    // El backend enviará jornada como objeto { id, nombre }. Mientras tanto puede
    // llegar como primitivo (ej. jornada: 43); en ese caso el id es ese valor y el
    // nombre queda como "---".
    jornada:
      data?.jornada && typeof data.jornada === "object"
        ? { id: data.jornada.id ?? null, nombre: data.jornada.nombre ?? "---" }
        : { id: data?.jornada ?? null, nombre: "---" },
    observacionJustificativa: data?.observacion_justificativa ?? "",
    observacionRevisor: data?.observacion_revisor ?? "",
    documentos: Array.isArray(data?.documentos) ? data.documentos.map(normalizeDocumento) : [],
    trazaEventos: Array.isArray(data?.traza_eventos) ? data.traza_eventos : [],
    productosAsociados: Array.isArray(data?.productos_asociados)
      ? data.productos_asociados.map(normalizeProductoAsociado)
      : [],
  };
};

// Vigencia de la jornada (flujo GESTIONAR / PRODUCTO INDETERMINADO).
// Consume /alertas/jornada/vigencia/?id_jornada= y devuelve la vigencia del response.
export const getAlertedProductsJornadaVigencia = async (idJornada) => {
  const response = await alertedProductsServices.getJornadaVigencia(idJornada);

  if (response?.status !== ResponseStatusEnum.OK) {
    throw response;
  }

  const data = response?.data ?? {};
  return data?.vigencia ?? data ?? null;
};

export const getAlertedProductsSolicitudes = async (filters = {}) => {
  const response = await alertedProductsServices.getEncabezados(
    buildSolicitudesQuery(filters)
  );

  if (response?.status !== ResponseStatusEnum.OK) {
    throw response;
  }

  // El endpoint /productos/encabezados responde un arreglo de encabezados.
  // Se conserva compatibilidad por si el backend lo envuelve en { encabezados, meta }.
  const data = response?.data ?? [];
  const encabezados = Array.isArray(data)
    ? data
    : Array.isArray(data?.encabezados)
    ? data.encabezados
    : [];

  const meta = Array.isArray(data)
    ? { total_registros: encabezados.length }
    : data?.meta ?? { total_registros: encabezados.length };

  return {
    meta,
    rows: encabezados.map(normalizeSolicitudRow),
  };
};
